import * as jobApi from '@/api/job';
import type { ClusterOverview } from '@/api/job';
import { ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Progress, Space, Tag, Tooltip } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * What a waiting user needs in order to read the cluster: which nodes exist, how many cores are
 * free right now, and how many jobs are queued.
 *
 * dral, from the Turin workshop (2026-09-09): "there is now way for the users to have an overview
 * of the status of the cluster ... no way to see the nodes overview, which one are free". The
 * participants had no terminal on prod, so `sinfo` was out of reach, and a job pending behind a
 * full partition looked exactly like a broken one.
 *
 * A failed read is shown AS a failure. The backend answers ok:false instead of an empty cluster;
 * this panel must not turn that back into "0 nodes, 0 cores" — the confident wrong answer to the
 * one question a waiting user is asking. After a good read, a later failure keeps the last reading
 * on screen and says it is stale, rather than blanking it.
 */
const REFRESH_MS = 30_000;

const nodeColor = (n: { available: boolean; cpus_idle: number; cpus_total: number }) =>
    !n.available ? 'default' : n.cpus_idle === 0 ? 'orange' : n.cpus_idle === n.cpus_total ? 'green' : 'blue';

const ClusterOverviewPanel = () => {
    const { t } = useTranslation('common');
    const [data, setData] = useState<ClusterOverview | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [readAt, setReadAt] = useState<Date | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await jobApi.getClusterOverview();
        if (res?.ok && res.data) {
            setData(res.data);
            setError(null);
            setReadAt(new Date());
        } else {
            setError(res?.message || t('cluster_overview.unavailable'));
        }
        setLoading(false);
    }, [t]);

    useEffect(() => {
        load();
        const id = window.setInterval(load, REFRESH_MS);
        return () => window.clearInterval(id);
    }, [load]);

    if (!data) {
        return error
            ? <Alert className='mb-3' type='warning' showIcon message={t('cluster_overview.unavailable')} description={error} />
            : null;
    }

    const used = data.total_cpus > 0 ? Math.round((100 * (data.total_cpus - data.idle_cpus)) / data.total_cpus) : 0;
    return (
        <div className='mb-3 rounded-md border border-solid border-neutral-200 dark:border-neutral-700 p-3'>
            <div className='flex justify-between items-center mb-2'>
                <Space size='large' wrap>
                    <span className='font-medium'>{t('cluster_overview.title')}</span>
                    <span>{t('cluster_overview.free_cores', { idle: data.idle_cpus, total: data.total_cpus })}</span>
                    <span>{t('cluster_overview.queue', { pending: data.pending_jobs, running: data.running_jobs })}</span>
                </Space>
                <Space>
                    {readAt && <span className='text-xs opacity-60'>{t('cluster_overview.read_at', { time: readAt.toLocaleTimeString() })}</span>}
                    <Tooltip title={t('cluster_overview.refresh')}>
                        <Button size='small' icon={<ReloadOutlined />} loading={loading} onClick={load} />
                    </Tooltip>
                </Space>
            </div>
            <Progress percent={used} size='small' showInfo={false} />
            {error && <div className='text-xs text-orange-500 mt-1'>{t('cluster_overview.stale', { error })}</div>}
            <div className='flex flex-wrap gap-1 mt-2'>
                {data.nodes.map((n) => (
                    <Tooltip
                        key={`${n.partition}/${n.name}`}
                        title={`${n.partition} · ${n.state}${n.reason ? ' · ' + n.reason : ''} · ${t('cluster_overview.cores_in_use', { alloc: n.cpus_alloc, total: n.cpus_total })}`}
                    >
                        <Tag color={nodeColor(n)}>
                            {n.name} {n.available ? `${n.cpus_idle}/${n.cpus_total}` : t('cluster_overview.unavailable_node')}
                        </Tag>
                    </Tooltip>
                ))}
            </div>
        </div>
    );
};

export default ClusterOverviewPanel;
